import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Mail, Book, Search, Clock } from 'lucide-react';

type SupportOption =
  | {
      title: string;
      description: string;
      icon: typeof Book;
      action: string;
      response: string;
      kind: 'route';
      to: string;
    }
  | {
      title: string;
      description: string;
      icon: typeof Mail;
      action: string;
      response: string;
      kind: 'mailto';
      href: string;
    };

export default function SupportPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const supportOptions: SupportOption[] = [
    {
      title: 'Documentation',
      description: 'Find answers in our comprehensive guides',
      icon: Book,
      action: 'Browse Docs',
      kind: 'route',
      to: '/docs',
      response: 'Instant',
    },
    {
      title: 'Email Support',
      description: 'Direct support from our team',
      icon: Mail,
      action: 'Send Email',
      kind: 'mailto',
      href: 'mailto:support@memara.io',
      response: '< 24 hours',
    },
  ];

  const faqItems = [
    {
      question: 'How do I get started with Memara?',
      answer:
        'Open the documentation hub at /docs for integration guides you can use today (for example REST API, Chrome extension, and ChatGPT). Sign in, create a space, then follow the setup for your tool from Integrations in the dashboard.',
      category: 'Getting Started',
    },
    {
      question: 'What integrations are available?',
      answer:
        'We support ChatGPT (Custom GPTs), Claude (Desktop + claude.ai connectors), Cursor, any MCP client, REST API, Zapier, n8n, and more. Browse the Integrations section on the documentation hub (/docs) for setup steps and auth requirements per integration.',
      category: 'Integrations',
    },
    {
      question: 'Is my data secure?',
      answer:
        'Memories are encrypted in transit and at rest. Each space is isolated.',
      category: 'Security',
    },
    {
      question: 'How does pricing work?',
      answer:
        'We offer a free tier with 1 memory space. Starter tier includes 3 spaces and 25,000 requests/month. Pro tier includes unlimited spaces and 250,000 requests/month.',
      category: 'Billing',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">How can we help you?</h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Get the support you need to build amazing AI applications with Memara.
            </p>

            {/* Search */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="search"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Support Options */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Choose Your Support Option</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {supportOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="w-16 h-16 mx-auto bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-indigo-600" />
                    </div>
                    <CardTitle className="text-xl">{option.title}</CardTitle>
                    <CardDescription className="text-base">{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{option.response}</span>
                    </div>
                    {option.kind === 'route' ? (
                      <Button asChild className="w-full">
                        <Link to={option.to}>{option.action}</Link>
                      </Button>
                    ) : (
                      <Button asChild className="w-full">
                        <a href={option.href}>{option.action}</a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{item.question}</CardTitle>
                    <Badge variant="outline">{item.category}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Still Need Help?</h2>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Our team is here to help you succeed. Reach out and we'll get back to you quickly.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button size="lg" asChild>
                  <a href="mailto:support@memara.io">Email Support</a>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to="/docs">Browse Documentation</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
